import { NavPage } from '@/components/breadcrumbs/Breadcrumbs';
import { CataloguePageHeader } from '@/components/catalogue/Header';
import { Button } from '@/components/form/Button';
import { ButtonVariant } from '@/components/form/buttonHelper';
import SiChevronLeft from '@/icons/SiChevronLeft';
import { useBundleStockLevels } from '@/services/catalogue/stockLevels/useBundleStockLevels';
import { useDefaultBundleStockLevels } from '@/services/catalogue/stockLevels/useDefaultBundleStockLevels';
import { useSaveInitialDefaultBundleStocks } from '@/services/catalogue/stockLevels/useSaveInitialDefaultBundleStocks';
import {
  useSaveAdditionalBundleStockLevel,
  useSetAdditionalBundleStockLevel
} from '@/services/catalogue/useCustomerCatalogueService';
import { useNavigateAndValidate } from '@/services/catalogue/validation/catalogueHooks';
import { useCustomerRoutePrefix } from '@/services/customer/useCustomerRoutePrefix';
import { FunctionComponent } from 'react';
import { CustomerCatalogueContainer } from '../../CustomerCatalogueContainer';
import { StockLevelsTable } from './StockLevelsTable';
import StockUserGuideButton from './StockUserGuideButton';
import { StockTableData } from './stockLevel.types';

const BundleStockLevelsContent: FunctionComponent = () => {
  const routePrefix = useCustomerRoutePrefix();
  const navigateAndValidate = useNavigateAndValidate();

  const breadcrumbs: NavPage[] = [
    {
      name: 'Stock Calculator',
      to: `${routePrefix}/stockCalculator`
    },
    {
      name: 'Bundle Stock Levels',
      to: ''
    }
  ];

  const stockLevels = useBundleStockLevels();
  const defaultStockLevels = useDefaultBundleStockLevels();

  const set = useSetAdditionalBundleStockLevel();
  const save = useSaveAdditionalBundleStockLevel();

  useSaveInitialDefaultBundleStocks();

  const onSave = async (data: StockTableData[]) => {
    if (!data) return;

    data.forEach(row => {
      set(row.productId, row.minStockLevel, row.maxStockLevel);
    });

    await save(data.map(row => row.productId));
  };

  return (
    <div className="flex h-full flex-1 flex-col bg-white bg-opacity-50">
      <CataloguePageHeader title={'Stock Calculator'} subtitle="Bundle Stock Levels" breadcrumbs={breadcrumbs}>
        <div className="flex gap-4">
          <StockUserGuideButton />
          <Button
            onClick={() => {
              navigateAndValidate(`${routePrefix}/advanced/stockCalculator`);
            }}
            variant={ButtonVariant.Mono}
          >
            <SiChevronLeft className="text-xl" />
            <span>Back to Bundle Stock Parameters</span>
          </Button>
          <Button
            onClick={() => {
              navigateAndValidate(`${routePrefix}/advanced/generalStockLevels`);
            }}
            variant={ButtonVariant.Primary}
          >
            <span>Set Additional Stock Levels</span>
          </Button>
        </div>
      </CataloguePageHeader>
      <div className="overflow-y-auto px-6 py-8 space-y-5 flex-1">
        {stockLevels.length === 0 ? (
          <div>There are no bundled products to display.</div>
        ) : (
          <StockLevelsTable stockLevels={stockLevels} onSaveCallback={onSave} resetStockLevels={defaultStockLevels} />
        )}
      </div>
    </div>
  );
};

export const BundleStockLevels: FunctionComponent = () => {
  return (
    <CustomerCatalogueContainer>
      <BundleStockLevelsContent />
    </CustomerCatalogueContainer>
  );
};
